import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Icon, Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';

/**
 * BreakdownPricing component.
 * Render breakdown pricing details with optional discounts and icons.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {string} props.label - The label for the pricing (e.g., passenger type or category).
 * @param {number} props.price - The numeric price value for calculations and display.
 * @param {string} [props.discount] - The primary discount percentage or value (optional).
 * @param {string} [props.subDiscount] - A secondary discount percentage or value (optional).
 * @param {boolean} [props.hasDiscount=false] - Indicates if there is a discount applied.
 * @param {boolean} [props.showDiscountBadge=false] - Shows the discount badge if true.
 * @param {string} [props.discountIcon] - Icon type to indicate a discount (optional).
 * @param {string} [props.typeIcon] - Icon type representing the passenger or item type (optional).
 * @param {string} [props.typeIconSize] - Render size for typeIcon (defaults to "S").
 *
 * @returns {JSX.Element} The rendered breakdown pricing component.
 */
const BreakdownPricing = ({
  label,
  price,
  discount,
  subDiscount,
  hasDiscount,
  showDiscountBadge,
  discountIcon,
  typeIcon,
  typeIconSize,
}) => {
  return (
    <Spacing justifyContent="space-between" alignItems="center">
      <Spacing size="XS" alignItems="center">
        {subDiscount ? (
          <div style={{ width: '15px' }} />
        ) : (
          typeIcon && <Icon type={typeIcon} size={typeIconSize} />
        )}

        <Text
          size="S"
          color={subDiscount ? 'grayMedium' : 'primary'}
          weight={subDiscount ? 'regular' : 'semibold'}
        >
          {label}
        </Text>

        {showDiscountBadge && (subDiscount || discount) && (
          <Badge smallPadding roundCorners type="gradient">
            <Text color="white" size="XS">
              {subDiscount || discount}
            </Text>
          </Badge>
        )}
      </Spacing>

      <Spacing alignItems="center" size="XS">
        {discountIcon ? <Icon size="S" type={discountIcon} /> : null}

        <LocalizedCurrency
          size="S"
          color="grayMedium"
          weight={subDiscount ? 'regular' : 'bold'}
          price={hasDiscount ? price * -1 : price}
          currencyStyle="currency"
        />
      </Spacing>
    </Spacing>
  );
};

BreakdownPricing.propTypes = {
  label: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  discount: PropTypes.string,
  subDiscount: PropTypes.string,
  hasDiscount: PropTypes.bool,
  showDiscountBadge: PropTypes.bool,
  discountIcon: PropTypes.string,
  typeIcon: PropTypes.string,
  typeIconSize: PropTypes.string,
};

BreakdownPricing.defaultProps = {
  discount: '',
  subDiscount: '',
  hasDiscount: false,
  showDiscountBadge: false,
  discountIcon: '',
  typeIcon: '',
  typeIconSize: 'S',
};

export default BreakdownPricing;
